import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder as FolderIcon, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import type { FileItem, Folder } from '@/types/api';
import { EmptyState } from '@/components/ui/EmptyState';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { FileRow } from './FileRow';

interface Props {
  files: FileItem[];
  workspaceId?: string;
  /** Child folders to show as rows above files. */
  folders?: Folder[];
  /** org/workspace ID used to build folder navigation links. */
  orgId?: string;
  /**
   * When provided alongside `onSelectionChange`, the table renders a checkbox
   * column on each row plus a select-all header checkbox.
   */
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  canManageFiles?: boolean;
  canDeleteFolders?: boolean;
  onContextMenu?: (e: React.MouseEvent, file: FileItem) => void;
  onFolderContextMenu?: (e: React.MouseEvent, folder: Folder) => void;
  onFolderAction?: (action: 'open' | 'info' | 'move' | 'delete', folder: Folder) => void;
}

export function FileTable({ files, workspaceId, folders = [], orgId, selectedIds, onSelectionChange, canManageFiles, canDeleteFolders, onContextMenu, onFolderContextMenu, onFolderAction }: Props) {
  const selectable = !!selectedIds && !!onSelectionChange;
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const totalRows = files.length + folders.length;
  const selectedCount = selectable
    ? [...files, ...folders.map((f) => ({ id: f.id }))].reduce(
        (acc, f) => (selectedIds!.has(f.id) ? acc + 1 : acc),
        0,
      )
    : 0;
  const allSelected = selectable && totalRows > 0 && selectedCount === totalRows;
  const someSelected = selectable && selectedCount > 0 && selectedCount < totalRows;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  // Keyboard navigation: ↑/↓ moves the highlighted row, Enter opens it,
  // Space toggles its selection (when in selectable mode). The whole tbody
  // is focusable as one unit so users tab into it once and then arrow.
  const [activeIdx, setActiveIdx] = useState(-1);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  // If files change underfoot, clamp the active index so it stays in range.
  useEffect(() => {
    if (activeIdx >= files.length && files.length > 0) {
      setActiveIdx(files.length - 1);
    }
  }, [activeIdx, files.length]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTableSectionElement>) => {
    if (files.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(files.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveIdx(files.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = files[activeIdx];
      if (target) navigate(`/files/${target.id}`);
    } else if (e.key === ' ' && selectable) {
      e.preventDefault();
      const target = files[activeIdx];
      if (target) toggleOne(target.id, !selectedIds!.has(target.id));
    }
  };

  if (files.length === 0 && folders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-16 text-center">
        <EmptyState
          title="No files yet"
          description="Drag and drop files here, or use the Upload button above."
        />
      </div>
    );
  }

  const toggleAll = (checked: boolean) => {
    if (!selectable) return;
    const next = new Set(selectedIds!);
    if (checked) {
      for (const f of files) next.add(f.id);
      for (const f of folders) next.add(f.id);
    } else {
      for (const f of files) next.delete(f.id);
      for (const f of folders) next.delete(f.id);
    }
    onSelectionChange!(next);
  };

  const toggleOne = (id: string, checked: boolean) => {
    if (!selectable) return;
    const next = new Set(selectedIds!);
    if (checked) next.add(id);
    else next.delete(id);
    onSelectionChange!(next);
  };

  return (
    <div className="overflow-x-auto custom-scrollbar">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
            {selectable && (
              <th className="py-2 pl-3 pr-1 w-8">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  aria-label={allSelected ? 'Deselect all files' : 'Select all files'}
                  className="accent-bb-navy"
                />
              </th>
            )}
            <th className="py-2 px-3 font-semibold">Name</th>
            {canManageFiles && <th className="py-2 px-3 font-semibold hidden sm:table-cell">Size</th>}
            <th className="py-2 px-3 font-semibold hidden sm:table-cell">Modified</th>
            {canManageFiles && <th className="py-2 px-3 font-semibold hidden lg:table-cell">Uploader</th>}
            <th className="py-2 px-3 font-semibold text-right sticky right-0 bg-white dark:bg-[#0E0E1F]">{canManageFiles ? 'Actions' : ''}</th>
          </tr>
        </thead>
        <tbody
          ref={tbodyRef}
          tabIndex={0}
          onKeyDown={onKeyDown}
          className="focus:outline-none focus:ring-1 focus:ring-bb-navy/30"
          aria-label="Files (use arrow keys to navigate, Enter to open, Space to select)"
        >
          {folders.map((folder, idx) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              orgId={orgId ?? ''}
              workspaceId={workspaceId}
              selected={selectable ? selectedIds!.has(folder.id) : undefined}
              onSelectChange={selectable ? (next) => toggleOne(folder.id, next) : undefined}
              isActive={idx === activeIdx}
              selectable={selectable}
              canManageFiles={canManageFiles}
              canDeleteFolders={canDeleteFolders}
              onContextMenu={onFolderContextMenu}
              onAction={onFolderAction}
            />
          ))}
          {files.map((f, idx) => (
            <FileRow
              key={f.id}
              file={f}
              workspaceId={workspaceId}
              selected={selectable ? selectedIds!.has(f.id) : undefined}
              onSelectChange={
                selectable ? (next) => toggleOne(f.id, next) : undefined
              }
              isActive={idx + folders.length === activeIdx}
              canManageFiles={canManageFiles}
              onContextMenu={onContextMenu}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FolderRow({
  folder,
  orgId,
  workspaceId,
  selected,
  onSelectChange,
  isActive,
  selectable,
  canManageFiles,
  canDeleteFolders,
  onContextMenu,
  onAction,
}: {
  folder: Folder;
  orgId: string;
  workspaceId?: string;
  selected?: boolean;
  onSelectChange?: (next: boolean) => void;
  isActive?: boolean;
  selectable?: boolean;
  canManageFiles?: boolean;
  canDeleteFolders?: boolean;
  onContextMenu?: (e: React.MouseEvent, folder: Folder) => void;
  onAction?: (action: 'open' | 'info' | 'move' | 'delete', folder: Folder) => void;
}) {
  const navigate = useNavigate();
  const to = `/workspace/${orgId}/folders/${folder.id}`;

  return (
    <tr
      onDoubleClick={() => navigate(to)}
      onContextMenu={onContextMenu ? (e) => { e.preventDefault(); onContextMenu(e, folder); } : undefined}
      className={`border-b border-slate-100 hover:bg-slate-50 group cursor-default${selected ? ' bg-bb-navy/5' : ''}${isActive ? ' ring-1 ring-bb-navy/40 ring-inset' : ''}`}
    >
      {selectable && (
        <td className="py-2 pl-3 pr-1 w-8">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => onSelectChange?.(e.target.checked)}
            aria-label={`Select ${folder.name}`}
            className="accent-bb-navy"
          />
        </td>
      )}
      <td className="py-2 px-2 sm:px-3 min-w-0 max-w-[50vw] sm:max-w-none">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="inline-flex items-center gap-2 text-slate-800 flex-1 min-w-0"
            title={folder.name}
          >
            <FolderIcon size={16} className="text-[#676687] shrink-0" />
            <span className="truncate font-medium">{folder.name}</span>
          </div>
          {/* Mobile-only action button — always visible in the name cell */}
          {onAction && (
            <div className="sm:hidden shrink-0">
              <Dropdown
                align="right"
                trigger={
                  <button
                    className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                    aria-label="Folder actions"
                  >
                    <MoreVertical size={16} />
                  </button>
                }
              >
                <DropdownItem onClick={() => onAction('open', folder)}>Open</DropdownItem>
                <DropdownItem onClick={() => onAction('info', folder)}>Info</DropdownItem>
                {canManageFiles && workspaceId && (
                  <DropdownItem onClick={() => onAction('move', folder)}>Move to folder</DropdownItem>
                )}
                {canDeleteFolders && (
                  <DropdownItem danger onClick={() => onAction('delete', folder)}>Delete</DropdownItem>
                )}
              </Dropdown>
            </div>
          )}
        </div>
      </td>
      {canManageFiles && <td className="py-2 px-3 text-sm text-slate-400 hidden sm:table-cell">—</td>}
      <td className="py-2 px-3 text-sm text-slate-600 whitespace-nowrap hidden sm:table-cell">
        {format(new Date(folder.updated_at), 'M/d/yyyy')}
      </td>
      {canManageFiles && (
        <td className="py-2 px-3 text-sm text-slate-500 hidden lg:table-cell">
          {folder.created_by_name ?? '—'}
        </td>
      )}
      <td className="hidden sm:table-cell py-2 px-3 text-right">
        {onAction && (
          <Dropdown
            align="right"
            trigger={
              <button
                className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700 transition-colors"
                aria-label="Folder actions"
              >
                <MoreVertical size={16} />
              </button>
            }
          >
            <DropdownItem onClick={() => onAction('open', folder)}>Open</DropdownItem>
            <DropdownItem onClick={() => onAction('info', folder)}>Info</DropdownItem>
            {canManageFiles && workspaceId && (
              <DropdownItem onClick={() => onAction('move', folder)}>Move to folder</DropdownItem>
            )}
            {canManageFiles && (
              <DropdownItem danger onClick={() => onAction('delete', folder)}>Delete</DropdownItem>
            )}
          </Dropdown>
        )}
      </td>
    </tr>
  );
}
